<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { vOutsideClick } from '../../directives/OutsideClick.directive';
import { useStoreTranslations } from '../../store/translations/translations';
import { useFloatingContactStore } from '../../store/contacts/floatingContact.ts';

import Button1XP from '../Button1XP/Button1XP.vue';
import ContactLink from './components/ContactLink/ContactLink.vue';
import ContactNavigation from './components/ContactNavigation/ContactNavigation.vue';
import ContactFinder from './components/ContactFinder/ContactFinder.vue';

import type { Link } from '../../types/link';
import type ContactCard from '../ContactCard/ContactCard.vue';

type ContactFinderProps = InstanceType<typeof ContactFinder>['$props'];
type ContactNavigationProps = InstanceType<typeof ContactNavigation>['$props']['navLinks'];
type ContactCardProps = InstanceType<typeof ContactCard>['$props'];

export interface ContactNode extends Link {
  children?: {
    title?: string;
    contacts?: ContactCardProps[];
    quickLinks?: ContactLinkProps[];
  };
}

export type ContactLinkProps = {
  defaultLink: ContactNode;
  tel?: boolean;
  mailto?: boolean;
  text?: string;
  showLink?: boolean;
  icon?: string;
};

const translationsStore = useStoreTranslations();
const stateStore = useFloatingContactStore();

const { translations } = storeToRefs(translationsStore);
const { isOpen, isL1, isL2 } = storeToRefs(stateStore);

const { fab_open, fab_close, backToL1, openFinder, openNavigation } = stateStore;

// TODO: use this
// const linkToContactForm = ref<ContactLinkProps | undefined>(undefined);
// const hasContactNavigation = ref<ContactLinkProps[] | undefined>(undefined);
// const contentFinder = ref<ContactFinderProps | undefined>(undefined);
// const contactNavigationData = ref<ContactNavigationProps | undefined>(undefined);
const linkToContactForm = inject<Ref<ContactLinkProps>>('linkToContactForm');
const remainingLinks = inject<Ref<ContactLinkProps[]>>('remainingLinks');
const contactFinderData = inject<Ref<ContactFinderProps>>('contactFinderData');
// TODO: contactFinderDataTwo is only for the story. Delete when BE is connected!
const contactFinderDataTwo = inject<Ref<ContactFinderProps>>('contactFinderDataTwo');
const contactNavigationData = inject<Ref<ContactNavigationProps>>('contactNavigationData');
const hasContactNavigation = ref<boolean>(true);

const label = computed(() => {
  return isL2.value
    ? contactFinderData?.value?.type === 'input'
      ? translations.value?.['contactFinderPerson.label'] || 'Find your WIKA Representative'
      : translations.value?.['contactFinderCountry.label'] || 'Ansprechperson finden'
    : translations.value?.['floatingContact.label'] || 'Need help?';
});

const floatingContactAriaLabel = computed(() => {
  return label.value || 'floating contact popup';
});
</script>
<template>
  <div
    id="floatingContactPanel"
    v-outside-click="fab_close"
    aria-labelledby="floatingContactAriaLabel"
    :class="[$style.container, isOpen && $style.opened]"
    role="dialog"
    title="floatingContactAriaLabel"
  >
    <div :class="[$style.header, isL2 && $style.center]">
      <Button1XP
        v-if="isOpen"
        :class="$style.back"
        :is-disabled="isL1"
        size="xs"
        :start-icon="isL2 ? 'chevron_left' : 'chat'"
        :start-icon-size="isL2 ? 'large' : 'xLarge'"
        @click="backToL1"
      />
      <h2 v-if="isOpen" :id="floatingContactAriaLabel" :class="isL2 && $style.h2_small">
        {{ label }}
      </h2>
      <Button1XP
        aria-controls="floatingContactPanel"
        :aria-expanded="isOpen"
        :class="$style.trigger"
        :size="isOpen ? 'xs' : 's'"
        :start-icon="isOpen ? 'close' : 'chat'"
        :start-icon-size="isOpen ? 'large' : 'medium'"
        :text="isOpen ? '' : label"
        @click="isOpen ? fab_close() : fab_open()"
      />
    </div>
    <section
      :aria-hidden="isOpen ? undefined : true"
      :class="[$style.content, isOpen && $style.opened, isL2 && $style.secondLevelOpened]"
    >
      <!-- TODO: remove the check and the second ContactFinder when contactFinderDataTwo is removed -->
      <ContactFinder
        v-if="contactFinderData && contactFinderData.type === 'input'"
        :placeholder="contactFinderData.placeholder"
        :results="contactFinderData.results"
        :text="contactFinderData.text"
        :title="contactFinderData.title"
        :type="contactFinderData.type"
        :validation-pattern="contactFinderData.validationPattern"
        @open-second-level="openFinder(contactFinderData.type)"
      />
      <ContactFinder
        v-if="contactFinderDataTwo && contactFinderDataTwo.type === 'select'"
        :options="contactFinderDataTwo.options"
        :options-title="contactFinderDataTwo.optionsTitle"
        :results="contactFinderDataTwo.results"
        :text="contactFinderDataTwo.text"
        :title="contactFinderDataTwo.title"
        :type="contactFinderDataTwo.type"
        @open-second-level="openFinder(contactFinderDataTwo.type)"
      />
      <ContactLink
        v-if="linkToContactForm"
        :icon="linkToContactForm.icon"
        :is-external="!!linkToContactForm.defaultLink?.url || undefined"
        :is-opened="isOpen"
        :src="linkToContactForm.defaultLink?.url || ''"
        :text="linkToContactForm.text"
        :title="linkToContactForm.defaultLink?.title || 'Contact form'"
      />
      <template v-if="hasContactNavigation">
        <ContactNavigation
          v-if="contactNavigationData"
          :nav-links="contactNavigationData"
          @open-second-level="openNavigation"
        />
      </template>
      <template v-if="remainingLinks">
        <ContactLink
          v-for="(link, index) in remainingLinks"
          :key="index"
          :icon="link.icon"
          :is-external="link.defaultLink?.url ? true : undefined"
          :is-mailto="link.mailto"
          :is-tel="link.tel"
          :show-link="link.showLink"
          :src="link.defaultLink?.url || link.defaultLink.src || ''"
          :text="link.text"
          :title="link.defaultLink?.title || ''"
        />
      </template>
    </section>
  </div>
</template>
<style lang="sass" module src="./FloatingContact.scss" />
